import type { LearnArticle } from '../types/learnTypes'

// Pre-listing reads, transcribed from the "Dashboard: Before you list" section
// of the Seller Portal dashboard content deck. Copy is the deck's, verbatim —
// only the typography (curly apostrophes, em dashes) is normalised to match the
// rest of the app. Don't rewrite a line here without the content owner: these
// make regulatory-adjacent claims (DLD valuation, KYC, settlement channels).
export const beforeListingArticles: LearnArticle[] = [
  {
    slug: 'reach-hundreds-of-investors',
    audience: 'before-listing',
    title: 'Reach hundreds of investors with one listing',
    lede: 'One property. Many buyers. Faster funding.',
    icon: 'users',
    blocks: [
      {
        kind: 'paragraph',
        text: 'When you sell the traditional way, your buyer pool is limited to people who can pay the full price of your property. That’s a small pool — and it’s why sales take months.',
      },
      {
        kind: 'paragraph',
        text: 'Tokenisation opens your property to a much larger audience: verified investors who can participate with smaller amounts.',
      },
      {
        kind: 'paragraph',
        text: 'Dubai’s tokenised property market has already shown how fast this model moves — early tokenised offerings in the market were fully funded within hours, some within minutes, drawing investors from dozens of nationalities.',
      },
      {
        kind: 'bullets',
        intro: 'Why demand works in your favour:',
        items: [
          {
            lead: 'Lower entry point = bigger buyer pool.',
            text: 'Investors don’t need the full property price to participate, so far more of them can say yes.',
          },
          {
            lead: 'Every investor is verified.',
            text: 'All participants complete regulatory KYC checks before they can invest. You never deal with unverified buyers.',
          },
          {
            lead: 'Transparent progress.',
            text: 'Once your seller request is approved, you can track progress directly from your dashboard.',
          },
        ],
      },
      {
        kind: 'closing',
        text: 'Your property doesn’t wait for the right buyer. It reaches all of them at once.',
      },
    ],
  },
  {
    slug: 'from-listing-to-funding',
    audience: 'before-listing',
    title: 'From listing to funding — here’s how it works',
    lede: 'Your first listing, in simple steps.',
    icon: 'list-check',
    blocks: [
      {
        kind: 'steps',
        items: [
          {
            label: 'Step 1',
            title: 'Submit your property',
            text: 'Share your property title deed and some required information. It takes minutes, not meetings.',
          },
          {
            label: 'Step 2',
            title: 'Verification',
            text: 'Your property and ownership are verified against Dubai Land Department records, and a valuation check is completed. We handle the coordination — you just track the status from your dashboard.',
          },
          {
            label: 'Step 3',
            title: 'Your offering goes live',
            text: 'Once approved, your property is listed to verified investors on the platform. You can follow funding progress in real time.',
          },
          {
            label: 'Step 4',
            title: 'Funding completes, you receive proceeds',
            text: 'When your offering is fully funded, the transaction settles through regulated channels and proceeds are transferred to your verified bank account.',
          },
        ],
      },
      {
        kind: 'closing',
        text: 'No open houses. No months of viewings. No chasing a single buyer.',
      },
    ],
  },
  {
    slug: 'traditional-vs-tokenised-sale',
    audience: 'before-listing',
    title: 'The traditional sale vs. the tokenised sale',
    lede: 'No brokers. No open houses. No months of waiting.',
    icon: 'arrows-h',
    blocks: [
      {
        kind: 'comparison',
        sides: [
          {
            label: 'The traditional way',
            text: 'Find a broker. Sign an agency agreement. Host viewings for weeks. Negotiate with one buyer at a time. Watch deals fall through at the last minute. Repeat until something sticks — often three to six months later, sometimes longer.',
          },
          {
            label: 'The tokenised way',
            text: 'Submit your property once. It’s verified against official records, valued, and listed to a pool of verified investors — all from your dashboard. Progress is visible in real time, and settlement happens through regulated channels.',
          },
        ],
      },
      {
        kind: 'bullets',
        intro: 'What you skip:',
        items: [
          {
            lead: 'Endless viewings.',
            text: 'Investors evaluate your property digitally through verified documentation, valuation data, and property details.',
          },
          {
            lead: 'One-buyer negotiations.',
            text: 'Your offering price is set upfront, anchored to an official DLD valuation — no back-and-forth haggling with a single party.',
          },
          {
            lead: 'Deal collapse risk.',
            text: 'A traditional sale dies if your one buyer walks away. A tokenised offering doesn’t depend on any single investor.',
          },
        ],
      },
      {
        kind: 'closing',
        text: 'Selling property used to be a second job. Now it’s a submission form and a dashboard.',
      },
    ],
  },
]
